import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectInternalNotifySeenFlights, setInternalNotify } from '_redux/slices/timeline/timeline-toast';
import moment from 'moment';
import { Spacing, Colors } from "_styles";
import * as TaskColors from "../utils/colors";
import { getBoardingOffsetMinutes } from "../utils/flight-time";
import FlightNumber from './flight-number';
import FlightStatusBadge from './job-card/flight-status-badge';
import HoverOverlay from './hover-overlay';
import BoardingFlightTimeModal from './boarding-flight-time-modal';
import { FaCirclePlus, FaCheckDouble } from "react-icons/fa6";
import { MdLock } from "react-icons/md";
import { GoShield } from "react-icons/go";

const getTimeValue = (isoDate) => {
    const timeValue = new Date(isoDate).getTime();
    return Number.isNaN(timeValue) ? null : timeValue;
};

const getBackgroundColor = (flightInfo, flight, type) => {
    if (!flightInfo && !flight && !type) return 'white';

    // if (flightInfo?.operatingStatus === "Cancelled" || flightInfo?.operatingStatus == 'cancelled' || flightInfo?.cancelled) return 'red';

    if (flight?.includes("ALTPickUp")) return TaskColors.ALTPickUps;

    if (flight?.includes("/boarding") || type === 'boarding') return "#e895a4";

    if (type === 'departure') return Colors.PRM_LIGHT_BLUE;

    if (type === 'arrival') return Colors.LIGHT_GREEN;

    return 'white';
};

const FlightInfoCard = ({
    iata,
    flight,
    flightKey,
    flightData,
    flightState,
    landedFlight,
    flightStatus,
    openModal,
    handleViewFlight,
    handleRightClick,
    hasEmptyPRMs,
    boarding
}) => {
    const backgroundColor = useMemo(
        () => getBackgroundColor(flightData, flight, flightState?.type),
        [flightData, flight, flightState?.type]
    );

    const isCancelled = flightData?.operatingStatus === "Cancelled" || flightData?.operatingStatus === 'cancelled' || flightData?.cancelled;

    const containerStyle = useMemo(() => ({
        backgroundColor,
        overflow: 'hidden',
        flex: 1,
        padding: iata === "JER" ? Spacing.SCALE_4 : 0,
        margin: 0,
        opacity: landedFlight ? 0.6 : 1.0,
        borderTopColor: isCancelled ? 'red' : 'transparent',
        borderTopWidth: isCancelled ? 5 : 0,
        borderTopStyle: isCancelled ? 'solid' : 'none',
    }), [backgroundColor, landedFlight, iata, isCancelled]);

    const isAltPickup = flight?.includes("ALTPickUp");
    const isBoarding = flight?.includes("/boarding") || boarding;

    // formatted time for flight card
    const formattedTime = useMemo(() => {

        // check if boarding time is locked
        if (flightData?.manualLockBoarding && flightData?.manualBoardingTime) {
            // use locked boarding time
            return moment(new Date(flightData.manualBoardingTime)).format("HH:mm");
        }

        // check if boarding time is set
        if (isBoarding && flightData?.boardingTime) {
            // use set boarding time
            return moment(new Date(flightData.boardingTime)).format("HH:mm");
        }

        // fallback to operation time
        if (!flightData?.operationTime) {
            return '';
        }

        const baseTime = moment(new Date(flightData.operationTime));

        if (isBoarding) {
            const offsetMinutes = getBoardingOffsetMinutes(flightData, iata);
            return baseTime.subtract(offsetMinutes, 'minutes').format("HH:mm");
        }

        return baseTime.format("HH:mm");

    }, [flightData?.operationTime, flightData?.boardingTime, flightData?.manualLockBoarding, flightData?.manualBoardingTime, flightData?.seats, flightData?.seatCapacity, flightData?.boardingOffsetMinutes, iata, isBoarding]);

    const hasGate = flightState?.statusGate && flightState.statusGate !== '' && flightState.statusGate !== 'N/A';
    const hasStand = flightState?.stand && flightState.stand !== '' && flightState.stand !== 'N/A';

    // backend flags ramp arrivals whose stand/gate no timeline area claims (shown in every area view)
    const unmappedBadge = flightData?.unmappedLocation ? (
        <span
            style={{ whiteSpace: 'nowrap', fontSize: 8, fontWeight: 'bold', padding: '0 3px', marginLeft: 3, borderRadius: 2.5, color: 'white', backgroundColor: '#C0392B' }}
            data-tippy-content="This stand isn't mapped to any timeline area - add it in Airport Locations"
        >
            NO AREA
        </span>
    ) : null;

    const dispatch = useDispatch();
    const internalNotifySeenFlights = useSelector(selectInternalNotifySeenFlights);
    const hasAlreadyQueuedNotification = useRef(false);
    const flightNotifyId = flightData?._id || flightData?.flightId || flight;
    const shouldShowBoardingAfterScheduleNotification = useMemo(() => {
        if (!flightData?.manualLockBoarding || !flightData?.manualBoardingTime) return false;
        const scheduledTime = getTimeValue(flightData?.scheduled);
        if (scheduledTime == null) return false;
        const boardingTime = getTimeValue(flightData?.manualBoardingTime);
        return boardingTime != null && boardingTime > scheduledTime;
    }, [flightData]);

    useEffect(() => {
        if (!flightNotifyId || !shouldShowBoardingAfterScheduleNotification) return;
        if (internalNotifySeenFlights.includes(flightNotifyId)) return;
        if (hasAlreadyQueuedNotification.current) return;

        dispatch(setInternalNotify({
            id: flightNotifyId,
            flightId: flightNotifyId,
            title: 'Boarding after scheduled time',
            body: `Flight ${flightState?.displayFlightNumber || flight} has a boarding time after its scheduled time.`,
        }));

        hasAlreadyQueuedNotification.current = true;
    }, [dispatch, flightNotifyId, flightState?.displayFlightNumber, flight, internalNotifySeenFlights, shouldShowBoardingAfterScheduleNotification]);

    const [showBoardingOverlay, setShowBoardingOverlay] = useState(false);
    const [overlayPosition, setOverlayPosition] = useState({ x: 0, y: 0 });
    const handleBoardingMouseEnter = useCallback((e) => {
        setShowBoardingOverlay(true);
        setOverlayPosition({ x: e.clientX, y: e.clientY });
    }, []);
    const handleBoardingMouseLeave = useCallback(() => setShowBoardingOverlay(false), []);
    const handleBoardingMouseMove = useCallback((e) => {
        setOverlayPosition({ x: e.clientX, y: e.clientY });
    }, []);

    return (
        <div
            onClick={handleViewFlight}
            onContextMenu={handleRightClick}
            onDoubleClick={handleRightClick}
            style={{ ...containerStyle, cursor: 'pointer', position: isBoarding ? 'relative' : undefined }}
        >
            {isBoarding && (
                <HoverOverlay
                    isVisible={showBoardingOverlay}
                    onMouseEnter={handleBoardingMouseEnter}
                    onMouseLeave={handleBoardingMouseLeave}
                    onMouseMove={handleBoardingMouseMove}
                    position={overlayPosition}
                    title="Boarding Flight"
                >
                    <BoardingFlightTimeModal flightData={flightData} flightState={flightState} iata={iata} />
                </HoverOverlay>
            )}
            {iata === "JER" ? (
                <div>
                    <div
                        style={{ display: 'flex', justifyContent: "space-between", flex: 1, cursor: 'pointer', padding: 0, margin: 0, borderBottomWidth: 0.5, borderBottomColor: 'white', borderBottomStyle: 'solid' }}
                        onClick={(e) => { e.stopPropagation(); openModal(); }}
                    >
                        <FlightNumber
                            flightNumber={`${flightState?.displayFlightNumber}-${flightState?.type === "arrival" ? flightState?.departureAirport : flightState?.arrivalAirport}${flightData?.aircraftType ? ' (' + flightData.aircraftType + ')' : ''}`}
                            codeShares={flightData?.CodeShareFlights}
                        />
                        {flightState?.completedFlight && <FaCheckDouble size={16} style={{ color: "#fff", marginLeft: Spacing.SCALE_12 }} />}
                        {!isAltPickup && (
                            <FaCirclePlus
                                onClick={(e) => { e.stopPropagation(); openModal(); }}
                                style={{ cursor: 'pointer' }}
                                color={flightState?.type === '' ? Colors.PRM_LIGHT_BLUE : "white"}
                                size={18}
                                data-tippy-content="Add Passenger"
                            />
                        )}
                    </div>

                    {isAltPickup ? (
                        <div className='mt-1' style={{ display: 'flex', flex: 1, padding: 0, margin: 0, borderBottomWidth: 0.5, borderBottomColor: 'white', borderBottomStyle: 'solid' }}>
                            <div>
                                <p style={{ textAlign: 'left', color: 'white', fontSize: "11px", fontWeight: "bolder", margin: 0 }}>{"ALTPickUp Pax"}</p>
                            </div>
                        </div>
                    ) : (
                        <FlightStatusBadge
                            openModal={openModal}
                            statusTime={flightState?.statusTime}
                            originalTime={flightState?.originalTime}
                            flight={flight}
                            operatingStatus={flightState?.operatingStatus}
                            flightStatus={flightStatus}
                        />
                    )}

                    <div style={{ display: 'flex', flex: 1, justifyContent: 'space-between', padding: 0, margin: 0 }}>
                        <div style={{ flex: 3, display: 'flex', justifyContent: 'space-between', padding: 0, margin: 0 }}>
                            {hasGate && (
                                <div style={{ display: 'flex', flex: 1, padding: 0, margin: 0 }}>
                                    <p style={{ color: 'white', padding: 0, margin: 0 }}>{(iata === 'JER' ? 'GATE: ' : 'G: ') + flightState?.statusGate}</p>
                                </div>
                            )}
                            {hasStand && (
                                <div style={{ display: 'flex', flex: 1, padding: 0, margin: 0 }}>
                                    <p style={{ color: 'white', padding: 0, margin: 0 }}>{(iata === 'JER' ? 'STAND: ' : 'S: ') + flightState?.stand}{unmappedBadge}</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            ) : (
                <div style={{ borderTopColor: landedFlight ? Colors.GRAY_DARK : "none", borderTopWidth: landedFlight ? 1 : 0, borderTopStyle: 'solid', overflow: 'hidden', flex: 1, padding: Spacing.SCALE_2, margin: 0 }}>

                    {/* top row */}
                    <div style={{ display: 'flex', justifyContent: "space-between", flex: 1, borderBottomWidth: 0.5, borderBottomColor: 'white', borderBottomStyle: 'solid' }}>

                        {/* flight number, arr/dep, aircraft reg */}
                        <div style={{ textAlign: 'left', color: 'white', fontWeight: 'bold', padding: 0, margin: 0, display: 'flex', alignItems: 'center' }}>
                            <FlightNumber
                                flightNumber={flightState?.displayFlightNumber}
                                codeShares={flightData?.CodeShareFlights}
                            />
                            <span
                                data-tippy-content={flightState?.type === "arrival" ? `Departure Airport: ${flightState?.departureAirport}` : `Arrival Airport: ${flightState?.arrivalAirport}`}
                                style={{ marginLeft: '1px', marginRight: '1px', fontSize: 14 }}
                            >
                                -{flightState?.type === "arrival" ? flightState?.departureAirport : flightState?.arrivalAirport}-
                            </span>
                            <span
                                data-tippy-content={iata === "JER" ? `Aircraft Type: ${flightData?.aircraftType || 'N/A'}` : `Aircraft Registration: ${flightData?.aircraftRegistration || 'N/A'}`}
                                style={{ marginLeft: '0px', fontSize: 14 }}
                            >
                                {iata === "JER"
                                    ? (flightData?.aircraftType ? `(${flightData.aircraftType})` : "")
                                    : (flightData?.aircraftRegistration ? flightData?.aircraftRegistration?.slice(-2) : "")}
                            </span>
                            {/* show third state icon if flight origin is from a third state */}
                            {
                                flightData?.thirdStateFlight && <div className='d-flex align-items-center'>
                                    <GoShield
                                        color={"White"}
                                        size={18}
                                        data-tippy-content="Third State IATA"
                                    />
                                </div>
                            }
                        </div>

                        {/* flight status */}
                        <div style={{ alignItems: 'center', display: 'flex', justifyContent: 'flex-end', padding: 0, margin: 0 }} data-tippy-content="Flight Status">
                            {flightState?.operatingStatus && !flightStatus && (
                                <p style={{
                                    whiteSpace: "nowrap",
                                    fontSize: 8,
                                    textAlign: 'center',
                                    padding: Spacing.SCALE_2,
                                    margin: 0,
                                    border: '1px solid',
                                    borderRadius: 2.5,
                                    borderColor: flightState?.operatingStatus == 'On Time' ? Colors.PRM_LIGHT_BLUE : 'white',
                                    color: flightState?.operatingStatus == 'On Time' ? Colors.PRM_LIGHT_BLUE : 'white',
                                    backgroundColor: flightState?.operatingStatus == 'Early' ? 'green' : flightState?.operatingStatus == 'Delay' ? 'red' : flightState?.operatingStatus == 'On Time' ? 'white' : flightState?.operatingStatus == 'Cancelled' ? 'red' : 'white'
                                }}>{flightState?.operatingStatus?.toUpperCase()}</p>
                            )}
                            {flightStatus && (
                                <p style={{ whiteSpace: "nowrap", fontSize: 8, textAlign: 'center', padding: Spacing.SCALE_2, margin: 0, border: '1px solid', borderRadius: 2.5, color: 'white', backgroundColor: 'green' }}>{flightStatus.toUpperCase()}</p>
                            )}
                        </div>
                    </div>

                    {/* bottom row */}
                    <div style={{ display: 'flex', justifyContent: "space-between", flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            <div style={{ display: 'flex', justifyContent: 'left', alignItems: 'left' }} data-tippy-content={`Flight Time`}>
                                <p style={{ textAlign: 'left', color: 'white', padding: 0, margin: 0 }}>{formattedTime ? formattedTime.replace(":", "") : ""}</p>
                            </div>
                            {((isBoarding && flightData?.manualLockBoarding) || (!isBoarding && flightData?.manualLock)) && (
                                <MdLock size={18} color="white" data-tippy-content="Boarding time locked" />
                            )}
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 7 }}>
                            {hasGate && (
                                <div>
                                    <p style={{ color: 'white', padding: 0, margin: 0 }} data-tippy-content="GATE">
                                        {`G${flightState?.statusGate}`}
                                    </p>
                                </div>
                            )}
                        </div>
                        <div>
                            {hasStand && (
                                <div>
                                    <p style={{ color: 'white', padding: 0, margin: 0 }} data-tippy-content="STAND">
                                        {`S${flightState?.stand}`}{unmappedBadge}
                                    </p>
                                </div>
                            )}
                            {!hasStand && unmappedBadge}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default React.memo(FlightInfoCard, (prevProps, nextProps) => {
    return (
        prevProps.flight === nextProps.flight &&
        prevProps.flightKey === nextProps.flightKey &&
        prevProps.flightData === nextProps.flightData &&
        prevProps.flightState === nextProps.flightState &&
        prevProps.landedFlight === nextProps.landedFlight &&
        prevProps.flightStatus === nextProps.flightStatus &&
        prevProps.iata === nextProps.iata &&
        prevProps.hasEmptyPRMs === nextProps.hasEmptyPRMs &&
        prevProps.boarding === nextProps.boarding
    );
});